import type { MessageSource } from "~/chat/types/chat";

export enum SseEventTypes {
  ANSWER = "answer",
  SOURCES = "sources",
  END = "end",
  MESSAGE_ID = "message_id",
  ERROR = "error",
  INFO = "info",
}

export interface SseAnswerResponse {
  token: string;
}

export interface SseSource {
  type: string;
  url: string;
  title: string;
}

export interface SseSourcesResponse {
  sources: MessageSource[];
}

export interface SseMessageIdResponse {
  message_id: number;
}

export type ResponseEvent =
  | CompletionEvent
  | SourcesEvent
  | MessageIdEvent
  | InfoEvent
  | ErrorEvent;

export type SseEventPayloads = {
  [SseEventTypes.ANSWER]: SseAnswerResponse;
  [SseEventTypes.SOURCES]: SseSourcesResponse;
  [SseEventTypes.MESSAGE_ID]: SseMessageIdResponse;
  [SseEventTypes.INFO]: SseInfoResponse;
  [SseEventTypes.ERROR]: SseErrorResponse;
  [SseEventTypes.END]: undefined;
};

type CompletionEvent = {
  event: SseEventTypes.ANSWER;
  message: string;
};

type SourcesEvent = {
  event: SseEventTypes.SOURCES;
  sources: MessageSource[];
};

type MessageIdEvent = {
  event: SseEventTypes.MESSAGE_ID;
  messageId: number;
};

export interface SseInfoResponse {
  step: string;
}

export interface SseErrorResponse {
  message?: string;
  error?: string;
}

type InfoEvent = {
  event: SseEventTypes.INFO;
  data: string;
};

type ErrorEvent = {
  event: SseEventTypes.ERROR;
  message: string;
};

function parseChunkField(rows: string[], fieldPrefix: string) {
  const fullPrefix = `${fieldPrefix}:`;
  return rows
    .find((it) => it.startsWith(fullPrefix))
    ?.slice(fullPrefix.length)
    ?.trim();
}

function isMalformedChunk(event: string | undefined, data: string | undefined) {
  return !event || !data;
}

function isSupportedChunk(event: SseEventTypes) {
  return Object.values(SseEventTypes).includes(event);
}

function readResponseSingeChunk(chunk: string): ResponseEvent | undefined {
  const rawChunkRows = chunk
    .split("\n")
    .map((it) => it.trim())
    .filter((it) => it !== "");

  if (rawChunkRows.length === 0) {
    return undefined;
  }

  const eventField = parseChunkField(rawChunkRows, "event");
  const dataField = parseChunkField(rawChunkRows, "data");

  if (isMalformedChunk(eventField, dataField)) {
    throw new Error("Malformed SSE chunk received from server");
  }

  if (!isSupportedChunk(eventField as SseEventTypes)) {
    console.warn(`Skipping non supported chunk: ${eventField}   ${dataField}`);
    return undefined;
  }

  if (eventField === SseEventTypes.END) {
    return undefined;
  }

  let data: SseEventPayloads[SseEventTypes];

  try {
    data = dataField && JSON.parse(dataField);
  } catch (err) {
    console.error(err);
    throw new Error("Failed to parse chunk result", { cause: err });
  }

  if (typeof data !== "object") {
    throw new Error("Failed to parse chunk result");
  }

  switch (eventField) {
    case SseEventTypes.ANSWER:
      return {
        event: SseEventTypes.ANSWER,
        message: (data as SseAnswerResponse).token,
      };
    case SseEventTypes.SOURCES:
      return {
        event: SseEventTypes.SOURCES,
        sources: (data as SseSourcesResponse).sources,
      };
    case SseEventTypes.MESSAGE_ID:
      if (typeof (data as SseMessageIdResponse).message_id !== "number") {
        throw new Error("message_id must be a number");
      }
      return {
        event: SseEventTypes.MESSAGE_ID,
        messageId: (data as SseMessageIdResponse).message_id,
      };
    case SseEventTypes.ERROR: {
      const errorData = data as SseErrorResponse;
      const errorMessage =
        errorData.message ||
        errorData.error ||
        "Failed to process your request";
      return {
        event: SseEventTypes.ERROR,
        message: errorMessage,
      };
    }
    case SseEventTypes.INFO:
      return {
        event: SseEventTypes.INFO,
        data: (data as SseInfoResponse).step,
      };
    default:
      console.warn("Unhandled SSE event type", eventField);
      return undefined;
  }
}

export function readResponseChunk(chunk: string): ResponseEvent[] {
  const rawChunksInBuffer = chunk.split("\n\n");

  return rawChunksInBuffer
    .map(readResponseSingeChunk)
    .filter(Boolean) as ResponseEvent[];
}
